// AuthContext.js - Simple authentication context for testing
import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing auth in localStorage
    const savedToken = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('authUser');
    
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  // Validate token and refresh profile on mount
  useEffect(() => {
    const validate = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app'}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.status === 401) {
          logout();
        }
      } catch (e) {
        // ignore network errors here
      }
    };
    validate();
  }, [token]);

  const login = async (email, password) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        // Handle the backend response format: data.data.user and data.data.token
        const userData = data.data?.user || data.user;
        const userToken = data.data?.token || data.token;
        
        if (userData && userToken) {
          setToken(userToken);
          setUser(userData);
          localStorage.setItem('authToken', userToken);
          localStorage.setItem('authUser', JSON.stringify(userData));
          return { success: true, user: userData };
        } else {
          return { success: false, error: 'Invalid response from server' };
        }
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  // Direct login with user data and token (for internal use)
  const loginWithUserData = (userData, userToken) => {
    setToken(userToken);
    setUser(userData);
    localStorage.setItem('authToken', userToken);
    localStorage.setItem('authUser', JSON.stringify(userData));
  };

  const logout = () => {
    // Clean up user-specific data before logging out
    const userId = user?._id || user?.id;
    if (userId) {
      localStorage.removeItem(`demoEnrollments_${userId}`);
    }
    
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
  };

  // Mock login for testing (bypasses backend authentication)
  const mockLogin = (role = 'admin') => {
    const mockUser = {
      _id: 'test-user-id',
      name: 'Test User',
      email: 'test@qorenova.com',
      role: role, // 'admin', 'instructor', or 'student'
    };
    
    // Create a simple JWT-like token for testing (not secure, just for demo)
    const mockToken = btoa(JSON.stringify({
      userId: 'test-user-id',
      role: role,
      exp: Date.now() + 24 * 60 * 60 * 1000 // 24 hours
    }));
    
    setUser(mockUser);
    setToken(mockToken);
    localStorage.setItem('authToken', mockToken);
    localStorage.setItem('authUser', JSON.stringify(mockUser));
  };

  const value = {
    user,
    token,
    loading,
    login,
    loginWithUserData,
    logout,
    mockLogin,
    isAuthenticated: !!token,
    isAdmin: user?.role === 'admin',
    isInstructor: user?.role === 'instructor',
    isStudent: user?.role === 'student',
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
